// It is being used at 2 place - Mweb Cart & Dineout Cart

// deps
import React, { memo, useMemo, RefObject, useCallback, useState, useContext } from "react";
import classNames from "classnames";

//internal deps
import { ScreenReaderOnly, AriaHidden, isEmpty, ErrorBoundary } from "@swiggy-private/core-ui";

// components
import Header from "./components/Header";
import OneIcon from "./components/OneIcon";
import BenefitList from "./components/BenefitList";
import PurchasePlan from "./components/PurchasePlan";
import SelectedPlan from "./components/SelectedPlan";
import { OneLogo } from "@super-ui/widgets/swiggy-one-logo";
import SuperViewDetailsFooter from "./components/SuperViewDetailsFooter";

// contexts
import { SuperContext } from "@super-ui/contexts";

// types
import { PlanDetailsItem, RenderingDetails } from "@swiggy-private/cart-types";
import { ONE_WIDGET_TEST_IDS } from "@super-ui/test-ids/one-widget";
import { AddSuperProps, DeliveryTagInfoUIData } from "@super-ui/interfaces";

// helpers
import { getPrefixedTestId } from "@super-ui/helpers/String";
import {
    shouldShowTitleShimmer,
    isTrialPlan,
    getOfferBadgeText,
    isOneTap,
    isSuperV3Benefits,
    isSuperV3Nudge,
    isOneLiteProgramAndType,
    getDeliveryTagInfoData,
} from "@super-ui/helpers/OneWidget";
import OfferBadge from "./components/OfferBadge";
import RemovePlan from "./components/RemovePlan";
import { SELECTED_PLAN_MODE } from "@super-ui/constants/SwiggyOne";

import styles from "./styles/SwiggyOneWidget.v4.scss";
import HeaderWithDeliveryInfo from "./components/HeaderWithDeliveryInfo";
import { MainContainer } from "./styles/styles";

export interface SwiggyOneWidgetProps {
    itemData: RenderingDetails;
    onItemClick?: (itemType: string, itemData?: RenderingDetails) => void;
    addSuper: (addSuperProps: AddSuperProps) => void;
    removeSuper: VoidFunction;
    changeSuper: (plan: PlanDetailsItem, ctaLink: string, isOneTap: boolean) => void;
    changeSuperBtnRef: RefObject<HTMLButtonElement> | RefObject<HTMLDivElement>;
    testIdPrefix?: string;
}

const SwiggyOneWidget: React.FC<SwiggyOneWidgetProps> = ({
    itemData,
    onItemClick,
    addSuper,
    removeSuper,
    changeSuper,
    changeSuperBtnRef,
    testIdPrefix,
}: SwiggyOneWidgetProps) => {
    const {
        analyticsEvents: {
            onImpressionSuperNudge,
            onImpressionSuperOneTapAddNudge,
            onImpressionSuperChangePlan,
            onImpressionSuperOfferBadge,
            onImpressionSuperRemovePlanConfirmationHalfCard,
            onImpressionSuperRemovePlanHalfCardBackButton,
            onImpressionSuperRemovePlanHalfCardRemoveButton,
            onClickSuperRemovePlanHalfCardBackButton,
            onClickSuperRemovePlanHalfCardRemoveButton,
            onClickRemoveSuper,
        },
    } = useContext(SuperContext);
    const { meta, display_text: displayText, key } = itemData;
    // we now have 2 plan_details key:
    // 1. active_plan_details: for the plan that is currently active
    // 2. plan_details: for the plan that is needed for upgrade scenarios or normal buying scenarios
    const {
        benefits = [],
        logo_ctx: logoCtx = { logo: "" },
        cta = {},
        active_plan_details: activePlan,
        plan_details: addNewPlan,
        chipText: chipText,
        headerColor,
        price: price,
        backgroundImage,
        showBorder,
        autoScaleWidth,
    } = meta || {};
    const [containerTestId, badgeTestId, oneLogoTestId] = useMemo(
        () => [
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.CONTAINER, testIdPrefix),
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.BADGE, testIdPrefix),
            getPrefixedTestId(ONE_WIDGET_TEST_IDS.ONELOGO, testIdPrefix),
        ],
        [testIdPrefix],
    );

    const isOneLitePlan = useMemo(() => !isEmpty(activePlan) && isOneLiteProgramAndType(meta), [activePlan, meta]);
    const deliveryTagUIInfo = useMemo(
        () => (isOneLitePlan ? (getDeliveryTagInfoData(meta, false) as DeliveryTagInfoUIData) : undefined),
        [isOneLitePlan, meta],
    );
    const isNudge = useMemo(() => isSuperV3Nudge(key), [key]);
    const isOneTrialPlan = useMemo(() => isNudge && isTrialPlan(meta), [meta, isNudge]);
    const isOneTapPlan = useMemo(() => isNudge && isOneTap(meta), [meta, isNudge]);
    const offerBadgeText = useMemo(() => getOfferBadgeText(meta), [meta]);
    const showTitleShimmer = useMemo(() => shouldShowTitleShimmer(key, isOneTrialPlan, meta), [
        key,
        isOneTrialPlan,
        meta,
    ]);

    const selectedPlanMode = useMemo(() => {
        // as this is new plan buy mode we will check the plan_details key
        // active_plan_details will be skipped as current active plan details are of no use as no upgrade journeys in M1
        if (meta?.plan_details && meta?.plan_details.plan_id) {
            if (isNudge && isOneTap(meta)) {
                return SELECTED_PLAN_MODE.ADD;
            } else if (isSuperV3Benefits(key) && !meta?.is_super) {
                return SELECTED_PLAN_MODE.REMOVE;
            }
        }
        return "";
    }, [isNudge, key, meta]);

    // if user is current active one user, it will be SUPER_V3_BENEFITS based widget
    const isOneActiveUser = !isNudge && !isOneLitePlan && !selectedPlanMode && isSuperV3Benefits(key);
    // used to hide or show grey bottom border on top header of one component, border acts as a seperator
    const isBenefitListPresent = useMemo(() => !isEmpty(benefits), [benefits]);

    const [showViewDetailsFooter, footerCommsData] = useMemo(() => {
        return [Boolean(selectedPlanMode) && !isEmpty(itemData?.meta?.header_note), itemData?.meta?.header_note];
    }, [itemData?.meta?.header_note, selectedPlanMode]);

    const mainContainerClass = classNames({
        [styles.mainContainer]: true,
        [styles.mainContainerWithConfetti]: isOneTrialPlan,
        [styles.mainContainerWithSubscriptionPlan]: !!selectedPlanMode,
        [styles.mainContainerWithBadgeOnTop]: isOneTapPlan,
        [styles.mainContainerWithDeliveryTagInfo]: !isEmpty(deliveryTagUIInfo),
        [styles.mainContainerWithDeliveryTagInfoLow]: !!deliveryTagUIInfo?.colorType?.isLow,
        [styles.mainContainerWithDeliveryTagInfoHigh]: !!deliveryTagUIInfo?.colorType?.isHigh,
        [styles.mainContainerWithChipText]: !isEmpty(chipText),
    });

    const logoContainerClass = classNames({
        [styles.logo]: true,
        [styles.logoSmall]: !selectedPlanMode,
        [styles.logoBig]: !!selectedPlanMode,
    });
    const topContainerClass = classNames({
        [styles.topContainer]: true,
        [styles.topContainerTrimWidth]: isOneTapPlan || isOneActiveUser,
    });
    const [showRemovePlanMessage, setShowRemovePlanMessage] = useState(false);
    const onRemovePlan = useCallback(
        (planId: string, planName: string, price: string) => {
            if (meta?.remove_plan_message) {
                setShowRemovePlanMessage(true);
                onClickRemoveSuper?.(planId, planName, price);
            } else {
                removeSuper();
            }
        },
        [meta, onClickRemoveSuper, removeSuper],
    );
    const onRemoveClose = useCallback(() => {
        setShowRemovePlanMessage(false);
    }, []);

    // ErrorBoundary to not break the cart page if there is any error in rendering this widget
    return (
        <ErrorBoundary errorContent={null}>
            <MainContainer
                className={mainContainerClass}
                data-testid={containerTestId}
                backgroundImage={backgroundImage ? backgroundImage : undefined}
            >
                {!isEmpty(offerBadgeText) && isOneTapPlan && (
                    <OfferBadge
                        text={offerBadgeText}
                        impressionSuperOfferBadge={onImpressionSuperOfferBadge}
                        testId={badgeTestId}
                        isOneTapPlan={isOneTapPlan}
                    />
                )}
                {!backgroundImage && !isOneTrialPlan && !isOneLitePlan && (
                    <AriaHidden as="div" className={logoContainerClass} data-testid={oneLogoTestId}>
                        <OneLogo />
                    </AriaHidden>
                )}
                {!isEmpty(chipText) && <div className={styles.chipContainer}>{chipText}</div>}
                <div className={topContainerClass}>
                    <ScreenReaderOnly text={displayText} />
                    <AriaHidden as="span" className={styles.topContainerIcon}>
                        <OneIcon />
                    </AriaHidden>
                    {/* OneLite Active User Widget has a usecase to show delivery count info at
                    right corner so for better UI handling created a new header structure */}
                    {isOneLitePlan || !isEmpty(deliveryTagUIInfo) ? (
                        <HeaderWithDeliveryInfo
                            title={displayText}
                            showTitleShimmer={showTitleShimmer}
                            logoCtx={logoCtx}
                            testIdPrefix={testIdPrefix}
                            isOneLitePlan={isOneLitePlan}
                            deliveryTagUIInfo={deliveryTagUIInfo}
                            isBenefitListPresent={isBenefitListPresent}
                        />
                    ) : (
                        <Header
                            title={displayText}
                            showTitleShimmer={showTitleShimmer}
                            logoCtx={logoCtx}
                            testIdPrefix={testIdPrefix}
                            isOneActiveUser={isOneActiveUser}
                            isBenefitListPresent={isBenefitListPresent}
                            chipText={chipText}
                            headerColor={headerColor}
                            showBorder={showBorder}
                            autoScaleWidth={autoScaleWidth}
                        />
                    )}
                </div>

                <BenefitList benefits={benefits} testIdPrefix={testIdPrefix} isOneLitePlan={isOneLitePlan} />

                {isNudge && !isOneTapPlan && (
                    <PurchasePlan
                        price={price}
                        itemData={itemData}
                        onItemClick={onItemClick}
                        testIdPrefix={testIdPrefix}
                        offerBadgeText={offerBadgeText}
                        isOneTapPlan={isOneTapPlan}
                        impressionSuperNudge={onImpressionSuperNudge}
                        impressionSuperOfferBadge={onImpressionSuperOfferBadge}
                    />
                )}
            </MainContainer>
            {selectedPlanMode && (
                <>
                    <SelectedPlan
                        itemData={itemData}
                        planDetails={addNewPlan}
                        cta={cta}
                        addSuper={addSuper}
                        removeSuper={onRemovePlan}
                        changeSuper={changeSuper}
                        changeSuperBtnRef={changeSuperBtnRef}
                        impressionSuperChangePlan={onImpressionSuperChangePlan}
                        impressionSuperOneTapAddNudge={onImpressionSuperOneTapAddNudge}
                        testIdPrefix={testIdPrefix}
                        mode={selectedPlanMode}
                        showViewDetailsFooter={showViewDetailsFooter}
                    />
                    {/* Footer to educate customers rebuying SwiggyOne */}
                    {showViewDetailsFooter && <SuperViewDetailsFooter footerComms={footerCommsData} />}
                </>
            )}
            {showRemovePlanMessage && (
                <RemovePlan
                    removePlanMessage={meta?.remove_plan_message}
                    planDetails={addNewPlan}
                    onRemove={removeSuper}
                    onClose={onRemoveClose}
                    impressionSuperRemovePlanConfirmationHalfCard={onImpressionSuperRemovePlanConfirmationHalfCard}
                    impressionSuperRemovePlanConfirmationHalfCardBackButton={
                        onImpressionSuperRemovePlanHalfCardBackButton
                    }
                    impressionSuperRemovePlanConfirmationHalfCardRemoveButton={
                        onImpressionSuperRemovePlanHalfCardRemoveButton
                    }
                    onClickSuperRemovePlanHalfCardBackButton={onClickSuperRemovePlanHalfCardBackButton}
                    onClickSuperRemovePlanHalfCardRemoveButton={onClickSuperRemovePlanHalfCardRemoveButton}
                ></RemovePlan>
            )}
        </ErrorBoundary>
    );
};

export default memo(SwiggyOneWidget);
